<template>
  <div class="change-password">
    <app-form-builder
      v-if="isMguuRespondent"
      v-model="fields"
      :handler="submitMguu"
      handler-button-text="Сохранить"
      @validation-error="$emit('notify', { type: 'error', message: $event })"
    >
      <template #prepend-buttons>
        <password-rules
          ref="passwordRules"
          class="change-password__rules"
          :password-for-check="newPasswordValue"
        />
      </template>
    </app-form-builder>

    <div v-else class="change-password__row">
      <form action="javascript:void(0);" autocomplete="off" class="change-password__form">
        <app-input-password
          autocomplete="password"
          v-model="payload.oldPassword"
          label="Старый пароль"
          :errors="v$.payload.oldPassword.$errors"
        />

        <app-input-password
          v-model="payload.newPassword"
          label="Новый пароль"
          autocomplete="new-password"
          :errors="v$.payload.newPassword.$errors"
          @input="v$.payload.newPassword.$validate()"
        />

        <app-input-password
          v-model="payload.confirmPassword"
          label="Подтвердите новый пароль"
          autocomplete="new-password"
          :errors="v$.payload.confirmPassword.$errors"
        />

        <mt-button
          :loading="requestPending"
          @click="submitDefault"
          size="large"
        >
          Изменить
        </mt-button>
      </form>
      <password-rules
        ref="passwordRules"
        class="change-password__rules"
        :password-for-check="payload.newPassword"
      />
    </div>
  </div>
</template>

<script>
import AuthApi from '@/api/auth.api.ts'
import PasswordRules from '@/components/workspace/auth/PasswordRules/index.vue'
import AppFormBuilder from '@/components/UI/AppFormBuilder.vue'
import mtButton from '@/components/UI/mtButton/index.vue'
import AppInputPassword from '@/components/UI/AppInputPassword.vue'
import useVuelidate from '@vuelidate/core'
import { helpers, sameAs } from '@vuelidate/validators'
import { useValidations } from '@/plugins/vuelidate-wrapper'
import { rules } from '@/services/validate.ts'

export default {
  name: 'ChangePassword',
  emits: ['notify'],
  components: { PasswordRules, AppFormBuilder, AppInputPassword, mtButton },
  data: () => ({
    requestPending: false,
    payload: {
      oldPassword: null,
      newPassword: null,
      confirmPassword: null,
    },
    fields: {
      oldPassword: {
        type: 'password',
        modelValue: null,
        key: 'oldPassword',
        bind: {
          label: 'Старый пароль *'
        },
        rules: {
          required: rules.required
        }
      },
      newPassword: {
        type: 'password',
        modelValue: null,
        key: 'newPassword',
        bind: {
          label: 'Новый пароль *'
        },
        rules: {
          required: rules.required
        }
      },
      confirmPassword: {
        type: 'password',
        modelValue: null,
        key: 'confirmPassword',
        bind: {
          label: 'Подтвердите новый пароль *'
        },
        rules: {
          required: rules.required
        }
      }
    }
  }),
  setup() {
    return {
      v$: useVuelidate({
        $lazy: true,
      }),
    }
  },
  validations() {
    const { required } = useValidations()
    return {
      payload: {
        oldPassword: { required },
        newPassword: {
          required,
          isValid: helpers.withMessage(
            'Пожалуйста, учтите все правила создания пароля',
            () => this.$refs.passwordRules?.allRulesAreValid,
          ),
        },
        confirmPassword: {
          required,
          sameAs: helpers.withMessage(
            'Пароли не совпадают',
            sameAs(this.payload.newPassword),
          ),
        },
      },
    }
  },
  computed: {
    isMguuRespondent() {
      return this.app_design_type === 'mguu' && this.$store.getters.authUserRoleSlug === 'respondent'
    },
    newPasswordValue() {
      return this.fields.newPassword?.modelValue ?? ''
    }
  },
  methods: {
    async submitMguu(groups) {
      const formFields = groups?.[0]?.fields ?? []
      const payload = formFields.reduce((acc, field) => {
        acc[field.key] = field.modelValue
        return acc
      }, {})

      this.fields.newPassword.modelValue = payload.newPassword ?? ''
      await this.$nextTick()

      if (!this.$refs.passwordRules?.allRulesAreValid) {
        this.$emit('notify', {
          type: 'error',
          message: 'Пожалуйста, учтите все правила создания пароля'
        })
        return
      }

      if (payload.newPassword !== payload.confirmPassword) {
        this.$emit('notify', {
          type: 'error',
          message: 'Пароли не совпадают'
        })
        return
      }

      try {
        await this.changePassword(payload)

        this.$emit('notify', {
          type: 'success',
          message: 'Пароль успешно изменен'
        })
        this.fields.oldPassword.modelValue = ''
        this.fields.newPassword.modelValue = ''
        this.fields.confirmPassword.modelValue = ''
      } catch (error) {
        this.$emit('notify', {
          type: 'error',
          message: 'Непредвиденная ошибка на сервере, пожалуйста повторите позже'
        })
      }
    },

    async submitDefault() {
      const isValidated = await this.v$.$validate()
      if (!isValidated) return

      this.requestPending = true
      this.changePassword(this.payload)
          .finally(() => {
            this.requestPending = false
            this.$toast.success('Пароль успешно изменен')
            this.payload = {
              oldPassword: '',
              newPassword: '',
              confirmPassword: '',
            }
            this.v$.$reset()
          })
    },

    async changePassword(payload) {
      return await AuthApi.changePassword(payload)
    }
  }
}
</script>

<style lang="scss">
.change-password {
  &__row {
    display: flex;
  }

  &__form {
    flex-grow: 1;
    padding-right: 30px;
  }

  .mt-button {
    width: 100%;
  }

  .mguu-form main .fields-grid {
    grid-template-columns: 1fr !important;
  }

  &__rules {
    height: fit-content;
    margin: 95px 0 0;
    border-radius: 5px;
  }
}
</style>
